import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'ProjectStatus':
    case 'ExistingOrNew':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };

    case 'DimDateKey':
    case 'ProjectFee':
    case 'WorkGeneratedAmount':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };

    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function WorkGeneratedTable({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  projects,
  clients,
  projecttypes,
  projectsubtypes,
  employees,
  selectedCompany,
  selectedDates,
  orgLength,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimProjectKey: 'All',
    DimDateKey: 'All',
    DimClientKey: 'All',
    DimProjectTypeKey: 'All',
    DimProjectSubTypeKey: 'All',
    DimEmployeeProjectSourceOneKey: 'All',
    DimEmployeeProjectSourceTwoKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    ProjectStatus: '',
    ProjectFee: '',
    WorkGeneratedAmount: '',
    ExistingOrNew: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;
    let startDate, endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate = new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    let filterRows = [];
    if (activeFilter) {
      filterRows = rows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate,
      );
    } else filterRows = rows;

    filterRows.sort((a, b) => b.DimDateKey - a.DimDateKey);

    setTableRows(filterRows);
  }, [rows, activeFilter, companies, selectedCompany]);

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimProjectKey',
      'DimDateKey',
      'DimClientKey',
      'DimProjectTypeKey',
      'DimProjectSubTypeKey',
      'DimEmployeeProjectSourceOneKey',
      'DimEmployeeProjectSourceTwoKey',
      'IsActive',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'ProjectFee' ||
      field.column.key === 'WorkGeneratedAmount'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (field.column.key === 'DimProjectKey') {
      let updated = {};
      let findProject = projects.find(
        (project) =>
          project[0] === updatedRows[field.indexes[0]][field.column.key],
      );

      updated['DimProjectKey'] = findProject[0];
      projects &&
        projects.forEach((cur) => {
          if (cur[0] === findProject[0]) {
            updated['DimClientKey'] = cur[3];
            updated['DimProjectTypeKey'] = cur[7];
            updated['DimProjectSubTypeKey'] = cur[8];
            updated['DimEmployeeProjectSourceOneKey'] = cur[9];
            updated['DimEmployeeProjectSourceTwoKey'] = cur[10];

            updatedRows[field.indexes[0]]['DimClientKey'] = cur[3];
            updatedRows[field.indexes[0]]['DimProjectTypeKey'] = cur[7];
            updatedRows[field.indexes[0]]['DimProjectSubTypeKey'] = cur[8];
            updatedRows[field.indexes[0]]['DimEmployeeProjectSourceOneKey'] =
              cur[9];
            updatedRows[field.indexes[0]]['DimEmployeeProjectSourceTwoKey'] =
              cur[10];
          }
        });

      updated_data.updated = Object.assign({}, updated);
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];
      updatedRows.map((row) => {
        if (selectedIds.has(row.id)) {
          row[field.column.key] =
            updatedRows[field.indexes[0]][field.column.key];

          if (field.column.key === 'DimProjectKey') {
            row['DimClientKey'] = updatedRows[field.indexes[0]]['DimClientKey'];
            row['DimProjectTypeKey'] =
              updatedRows[field.indexes[0]]['DimProjectTypeKey'];
            row['DimProjectSubTypeKey'] =
              updatedRows[field.indexes[0]]['DimProjectSubTypeKey'];
            row['DimEmployeeSupervisorKey'] =
              updatedRows[field.indexes[0]]['DimEmployeeSupervisorKey'];
            row['DimEmployeeProjectSourceOneKey'] =
              updatedRows[field.indexes[0]]['DimEmployeeProjectSourceOneKey'];
            row['DimEmployeeProjectSourceTwoKey'] =
              updatedRows[field.indexes[0]]['DimEmployeeProjectSourceTwoKey'];
          }
        }
        return row;
      });
    }

    const mergedRows = mergeEditedGridRows(
      tableRows,
      updatedRows,
      updated_data.rowIds,
      updated_data.updated,
    );

    if (field.column.key === 'DimDateKey') {
      mergedRows.sort((a, b) => b.DimDateKey - a.DimDateKey);
      getStateData(mergedRows);
    }

    setTableRows(mergedRows);
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };

  const workGeneratedColumns = useMemo(() => {
    let work_generated_columns = [
      SelectColumn,
      {
        key: 'DimProjectKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project</b>
            {frozenColumns.indexOf('DimProjectKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimProjectKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimProjectKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 175,
        resizable: true,
        frozen: frozenColumns.indexOf('DimProjectKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projects &&
                  projects.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimProjectKey ? props.row.DimProjectKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projects &&
                projects.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        resizable: true,
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimClientKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Client</b>
            {frozenColumns.indexOf('DimClientKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 175,
        resizable: true,
        frozen: frozenColumns.indexOf('DimClientKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimClientKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimClientKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimClientKey ? props.row.DimClientKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimClientKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {clients &&
                clients.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectTypeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Type</b>
            {frozenColumns.indexOf('DimProjectTypeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectTypeKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectTypeKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 150,
        resizable: true,
        frozen:
          frozenColumns.indexOf('DimProjectTypeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectTypeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectTypeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimProjectTypeKey ? props.row.DimProjectTypeKey : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectTypeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projecttypes &&
                projecttypes.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectSubTypeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Sub Type</b>
            {frozenColumns.indexOf('DimProjectSubTypeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectSubTypeKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectSubTypeKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 175,
        resizable: true,
        frozen:
          frozenColumns.indexOf('DimProjectSubTypeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectSubTypeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectSubTypeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimProjectSubTypeKey
                  ? props.row.DimProjectSubTypeKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectSubTypeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projectsubtypes &&
                projectsubtypes.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectSourceOneKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 1</b>
            {frozenColumns.indexOf('DimEmployeeProjectSourceOneKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceOneKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceOneKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 175,
        resizable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectSourceOneKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectSourceOneKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectSourceOneKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectSourceOneKey
                  ? props.row.DimEmployeeProjectSourceOneKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectSourceOneKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectSourceTwoKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 2</b>
            {frozenColumns.indexOf('DimEmployeeProjectSourceTwoKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceTwoKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceTwoKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 175,
        resizable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectSourceTwoKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectSourceTwoKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectSourceTwoKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectSourceTwoKey
                  ? props.row.DimEmployeeProjectSourceTwoKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectSourceTwoKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'ProjectStatus',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Status</b>
            {frozenColumns.indexOf('ProjectStatus') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectStatus')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectStatus')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ProjectStatus')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ProjectStatus',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 100,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ProjectStatus}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ProjectStatus: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ProjectStatus') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'ProjectFee',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Fee</b>
            {frozenColumns.indexOf('ProjectFee') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectFee')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectFee')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ProjectFee')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'ProjectFee')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 100,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ProjectFee}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ProjectFee: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ProjectFee') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ProjectFee}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ProjectFee: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'WorkGeneratedAmount',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Work Generated Amount</b>
            {frozenColumns.indexOf('WorkGeneratedAmount') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'WorkGeneratedAmount')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'WorkGeneratedAmount')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'WorkGeneratedAmount',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'WorkGeneratedAmount',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 245,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.WorkGeneratedAmount}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    WorkGeneratedAmount: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('WorkGeneratedAmount') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.WorkGeneratedAmount}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                WorkGeneratedAmount: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ExistingOrNew',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source</b>
            {frozenColumns.indexOf('ExistingOrNew') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ExistingOrNew')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ExistingOrNew')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ExistingOrNew')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ExistingOrNew',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 105,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ExistingOrNew}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ExistingOrNew: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ExistingOrNew') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'IsActive',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Active</b>
            {frozenColumns.indexOf('IsActive') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        frozen: frozenColumns.indexOf('IsActive') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsActive}
              onChange={(event) => {
                props.onRowChange(
                  {
                    ...props.row,
                    IsActive: event.target.checked,
                  },
                  true,
                );
              }}
              color="success"
            />
          </div>
        ),
      },
    ];

    if (orgLength.length > 1) {
      work_generated_columns.splice(2, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 205,
        resizable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      work_generated_columns.splice(2, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 205,
        resizable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = work_generated_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = work_generated_columns.filter(
      (col) => col.frozen === false,
    );
    work_generated_columns = [...frozenCols, ...unfrozenCols];

    return work_generated_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    orgLength,
    projects,
    dates,
    clients,
    employees,
    projecttypes,
    projectsubtypes,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimProjectKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectKey == filters.DimProjectKey
          : true) &&
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimClientKey !== 'All'
          ? // eslint-disable-next-line
            r.DimClientKey == filters.DimClientKey
          : true) &&
        (filters.DimProjectTypeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectTypeKey == filters.DimProjectTypeKey
          : true) &&
        (filters.DimProjectSubTypeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectSubTypeKey == filters.DimProjectSubTypeKey
          : true) &&
        (filters.DimEmployeeProjectSourceOneKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectSourceOneKey ==
            filters.DimEmployeeProjectSourceOneKey
          : true) &&
        (filters.DimEmployeeProjectSourceTwoKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectSourceTwoKey ==
            filters.DimEmployeeProjectSourceTwoKey
          : true) &&
        (filters.ProjectFee
          ? r.ProjectFee?.toString().includes(filters.ProjectFee.toString())
          : true) &&
        (filters.WorkGeneratedAmount
          ? r.WorkGeneratedAmount?.toString().includes(
              filters.WorkGeneratedAmount.toString(),
            )
          : true) &&
        (filters.ProjectStatus
          ? r.ProjectStatus?.toLowerCase().includes(
              filters.ProjectStatus.toLowerCase(),
            )
          : true) &&
        (filters.ExistingOrNew
          ? r.ExistingOrNew?.toLowerCase().includes(
              filters.ExistingOrNew.toLowerCase(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportData = useMemo(() => {
    let export_data = [];

    if (exportAllData) {
      export_data = [...tableRows];
    } else {
      if (startDate && endDate) {
        let start_date =
          new Date(startDate).getFullYear() * 10000 +
          (new Date(startDate).getMonth() + 1) * 100 +
          1;
        let end_date =
          new Date(endDate).getFullYear() * 10000 +
          (new Date(endDate).getMonth() + 1) * 100 +
          1;
        export_data = [...tableRows];
        export_data = export_data.filter(
          (data) =>
            data.DimDateKey >= start_date && data.DimDateKey <= end_date,
        );
      }
    }

    return export_data;
  }, [exportAllData, tableRows, startDate, endDate]);

  const exportValue = useMemo(() => {
    let export_value = {
      'Work Generated': {
        columns: [],
        data: [],
      },
      Project: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Date: {
        columns: [],
        data: [],
      },
      Client: {
        columns: [],
        data: [],
      },
      Employee: {
        columns: [],
        data: [],
      },
      'Project Type': {
        columns: [],
        data: [],
      },
      'Project Sub Type': {
        columns: [],
        data: [],
      },
    };

    export_value['Work Generated'].columns = [
      { title: 'DimProjectKey', width: { wpx: 80 } },
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'DimDateKey', width: { wpx: 80 } },
      { title: 'DimClientKey', width: { wpx: 80 } },
      { title: 'DimProjectTypeKey', width: { wpx: 80 } },
      { title: 'DimProjectSubTypeKey', width: { wpx: 80 } },
      { title: 'DimEmployeeProjectSourceOneKey', width: { wpx: 80 } },
      { title: 'DimEmployeeProjectSourceTwoKey', width: { wpx: 80 } },
      { title: 'ProjectStatus', width: { wpx: 80 } },
      { title: 'ProjectFee', width: { wpx: 80 } },
      { title: 'WorkGeneratedAmount', width: { wpx: 80 } },
      { title: 'IsActive', width: { wpx: 80 } },
    ];
    exportData.forEach((data) => {
      export_value['Work Generated'].data.push([
        {
          value: data.DimProjectKey ? Number(data.DimProjectKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimDateKey ? Number(data.DimDateKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimClientKey ? Number(data.DimClientKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimProjectTypeKey ? Number(data.DimProjectTypeKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimProjectSubTypeKey
            ? Number(data.DimProjectSubTypeKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeProjectSourceOneKey
            ? Number(data.DimEmployeeProjectSourceOneKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeProjectSourceTwoKey
            ? Number(data.DimEmployeeProjectSourceTwoKey)
            : -1,
          style: { numFmt: '0' },
        },
        { value: data.ProjectStatus ? data.ProjectStatus : '' },
        {
          value: data.ProjectFee ? Number(data.ProjectFee) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.WorkGeneratedAmount
            ? Number(data.WorkGeneratedAmount)
            : 0,
          style: { numFmt: '0.00' },
        },
        { value: data.IsActive ? 'true' : 'false' },
      ]);
    });

    export_value.Project.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'ProjectName', width: { wpx: 80 } },
    ];

    projects &&
      projects.forEach((cur) => {
        export_value.Project.data.push([
          { value: cur[0], style: { numFmt: '0.00' } },
          { value: cur[1] },
        ]);
      });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Date.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Date', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value.Date.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]);
      });

    export_value.Client.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    clients &&
      clients.forEach((cur) => {
        export_value.Client.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value.Employee.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    employees &&
      employees.forEach((cur) => {
        export_value.Employee.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Project Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    projecttypes &&
      projecttypes.forEach((cur) => {
        export_value['Project Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Project Sub Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    projectsubtypes &&
      projectsubtypes.forEach((cur) => {
        export_value['Project Sub Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });
    return export_value;
  }, [
    exportData,
    projects,
    companies,
    organisations,
    dates,
    clients,
    employees,
    projecttypes,
    projectsubtypes,
  ]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <Button
          onClick={() => onClickedAdd(name, 'auto')}
          variant="outlined"
          color="success"
        >
          Auto Add
        </Button>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={workGeneratedColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}          
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel filename="Work Generated" value={exportValue} />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

WorkGeneratedTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  projects: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  projecttypes: PropTypes.array.isRequired,
  projectsubtypes: PropTypes.array.isRequired,
  employees: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default WorkGeneratedTable;
